<template>
  <div class="country-combobox" ref="comboboxRoot">
    <div
      class="country-combobox__input-wrapper form-input mt-2"
      :class="{ 'invalid': invalid, 'country-combobox__input-wrapper--disabled': disabled }"
      @click="openDropdown"
    >
      <img
        v-if="modelValue"
        :src="flagUrl(modelValue)"
        :alt="modelValue"
        class="country-combobox__flag"
        aria-hidden="true"
      />
      <input
        ref="inputEl"
        type="text"
        role="combobox"
        :value="inputDisplay"
        :placeholder="placeholder"
        :disabled="disabled"
        :aria-expanded="isOpen"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        :aria-activedescendant="activeDescendant"
        autocomplete="off"
        class="country-combobox__input"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
      />
    </div>
    <ul
      v-show="isOpen"
      ref="listboxEl"
      role="listbox"
      class="country-combobox__dropdown"
    >
      <li
        v-for="(country, index) in filteredCountries"
        :key="country.code"
        :id="'country-option-' + country.code"
        role="option"
        :aria-selected="country.code === modelValue"
        class="country-combobox__option"
        :class="{
          'country-combobox__option--active': index === activeIndex,
          'country-combobox__option--selected': country.code === modelValue
        }"
        @mousedown.prevent="selectCountry(country.code)"
        @mouseenter="activeIndex = index"
      >
        <img
          :src="flagUrl(country.code)"
          :alt="country.code"
          class="country-combobox__flag"
          aria-hidden="true"
        />
        <span>{{ country.name }}</span>
      </li>
      <li v-if="filteredCountries.length === 0" class="country-combobox__no-results">
        {{ $t('address.noCountriesFound') }}
      </li>
    </ul>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCountries, getCountryName } from '../countries.js';

export default {
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    invalid: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    }
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { locale } = useI18n();
    const inputEl = ref(null);
    const listboxEl = ref(null);
    const comboboxRoot = ref(null);
    const isOpen = ref(false);
    const searchQuery = ref('');
    const activeIndex = ref(-1);
    const isSearching = ref(false);

    const countries = computed(() => getCountries(locale.value));

    const selectedName = computed(() => {
      if (!props.modelValue) return '';
      return getCountryName(props.modelValue, locale.value);
    });

    const inputDisplay = computed(() => {
      if (isSearching.value) return searchQuery.value;
      return selectedName.value;
    });

    const filteredCountries = computed(() => {
      if (!isSearching.value || !searchQuery.value) return countries.value;
      const q = searchQuery.value.toLowerCase();
      return countries.value.filter(c => c.name.toLowerCase().includes(q));
    });

    const activeDescendant = computed(() => {
      if (activeIndex.value < 0 || activeIndex.value >= filteredCountries.value.length) return undefined;
      return 'country-option-' + filteredCountries.value[activeIndex.value].code;
    });

    function flagUrl(code) {
      return `https://hatscripts.github.io/circle-flags/flags/${code.toLowerCase()}.svg`;
    }

    function openDropdown() {
      if (props.disabled) return;
      isOpen.value = true;
      inputEl.value?.focus();
    }

    function onFocus() {
      if (props.disabled) return;
      isOpen.value = true;
      isSearching.value = true;
      searchQuery.value = '';
      activeIndex.value = -1;
    }

    function onBlur() {
      // Small delay to allow mousedown on option to fire
      setTimeout(() => {
        isOpen.value = false;
        isSearching.value = false;
        searchQuery.value = '';
        activeIndex.value = -1;
      }, 150);
    }

    function onInput(event) {
      searchQuery.value = event.target.value;
      isSearching.value = true;
      activeIndex.value = filteredCountries.value.length > 0 ? 0 : -1;
    }

    function selectCountry(code) {
      emit('update:modelValue', code);
      isOpen.value = false;
      isSearching.value = false;
      searchQuery.value = '';
      activeIndex.value = -1;
    }

    function scrollActiveIntoView() {
      nextTick(() => {
        if (!listboxEl.value) return;
        const activeEl = listboxEl.value.children[activeIndex.value];
        if (activeEl) {
          activeEl.scrollIntoView({ block: 'nearest' });
        }
      });
    }

    function onKeydown(event) {
      const list = filteredCountries.value;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen.value) {
            isOpen.value = true;
            isSearching.value = true;
            searchQuery.value = '';
          }
          activeIndex.value = activeIndex.value < list.length - 1 ? activeIndex.value + 1 : 0;
          scrollActiveIntoView();
          break;

        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen.value) {
            isOpen.value = true;
            isSearching.value = true;
            searchQuery.value = '';
          }
          activeIndex.value = activeIndex.value > 0 ? activeIndex.value - 1 : list.length - 1;
          scrollActiveIntoView();
          break;

        case 'Enter':
          event.preventDefault();
          if (isOpen.value && activeIndex.value >= 0 && activeIndex.value < list.length) {
            selectCountry(list[activeIndex.value].code);
          }
          break;

        case 'Escape':
          event.preventDefault();
          isOpen.value = false;
          isSearching.value = false;
          searchQuery.value = '';
          activeIndex.value = -1;
          break;

        case 'Tab':
          if (isOpen.value && activeIndex.value >= 0 && activeIndex.value < list.length) {
            selectCountry(list[activeIndex.value].code);
          }
          isOpen.value = false;
          isSearching.value = false;
          break;
      }
    }

    // When locale changes, the display updates automatically via computed properties

    return {
      inputEl,
      listboxEl,
      comboboxRoot,
      isOpen,
      activeIndex,
      inputDisplay,
      filteredCountries,
      activeDescendant,
      flagUrl,
      openDropdown,
      onFocus,
      onBlur,
      onInput,
      onKeydown,
      selectCountry
    };
  }
};
</script>

<style scoped>
.country-combobox {
  position: relative;
font-size: var(--font-size-normal);
  
}

.country-combobox__input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 10px;
}

.country-combobox__input-wrapper--disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.country-combobox__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  color: inherit;
  padding: 0;
  min-width: 0;
}

.country-combobox__input:disabled {
  cursor: not-allowed;
}

.country-combobox__flag {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
}

.country-combobox__dropdown {
  position: absolute;
  z-index: 50;
  left: 0;
  right: 0;
  margin-top: 4px;
  max-height: 240px;
  overflow-y: auto;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
  list-style: none;
  padding: 4px 0;
}

.country-combobox__option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
}

.country-combobox__option--active {
  background-color: #f3f4f6;
}

.country-combobox__option--selected {
  font-weight: 600;
}

.country-combobox__no-results {
  padding: 10px;
  font-size: 0.875rem;
  color: #6b7280;
}
</style>
